import { mutateConsumeSubsidiary } from "@/api/consumeSubsidiary/useConsumeSubsidiary";
import { inventories } from "@/api/inventories/useInventoriesQuery";
import { ItemAutoComplete } from "@/custom/features/ui/autoComplete/item/item-autoComplete";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { InventoriesAutoComplete } from "@/features/ui/autoComplete/inventoryLot/inventory-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";

import { Button, Flex, Input, Table, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  ItemsGet200ResponseRowsInnerItemTypeEnum,
  ProductionActionApiWorksWorkIdBulkConsumeSubsidiaryPutRequest,
  WorksWorkIdBulkConsumeSubsidiaryPut200ResponseFailedInner,
  WorksWorkIdBulkConsumeSubsidiaryPut200ResponseSuccessInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";

const MultiPigmentInput = Object.assign({}, FormMain, {
  Button: Button,
  Wrapper: FormWrapper,
  Flex: Flex,
  Text: Text,
  TextInput: TextInput,
  Input: Input,
  ItemAutoComplete: ItemAutoComplete,
  LocationAutoComplete: LocationAutoComplete,
  InventoriesAutoComplete: InventoriesAutoComplete,
});

interface MultiPigmentInputFormProps {
  workId: number | undefined;
}

type FormItem = {
  itemCode: string;
  lotId: string;
  locationCode: string;
  quantity: string;
  locationName?: string;
};

export const getLotId = (compositeKey: string | null | undefined): string | null => {
  if (!compositeKey) return null;
  return compositeKey.split("_")[0];
};

const getLocationCode = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return value.split("_").slice(1).join("_");
};

export const MultiPigmentInputForm = ({ workId }: MultiPigmentInputFormProps) => {
  const { closeModal } = useModal();

  const { mutate: subsidiaryMutate } = useMutation(
    (params: ProductionActionApiWorksWorkIdBulkConsumeSubsidiaryPutRequest) =>
      mutateConsumeSubsidiary
        .bulkConsume(params)
        .mutationFn(params as ProductionActionApiWorksWorkIdBulkConsumeSubsidiaryPutRequest | any),
  );

  const formData = useForm<{
    currentItem: FormItem;
    items: FormItem[];
  }>({
    initialValues: {
      currentItem: {
        itemCode: "",
        lotId: "",
        locationCode: "",
        quantity: "",
        locationName: "",
      },
      items: [],
    },
  });
  const [lotId, setLotId] = useState<string>("");

  let tempLotId = "";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setLotId(tempLotId);
    } else {
      tempLotId += e.key;
    }
  };

  const hasAlertedRef = useRef(false);

  // 바코드를 입력했을 때, 해당 LOT 정보가 있는지 확인하는 쿼리
  useQuery({
    ...inventories.get({
      query: lotId
        ? {
            $and: [{ lotId: { $eq: lotId } }, { quantity: { $gt: 0 } }],
          }
        : {},
    }),
    enabled: !!lotId,
    onSuccess: (response) => {
      const lotData = response.data.rows?.[0];

      if (!lotData) {
        if (!hasAlertedRef.current) {
          alert("해당 LOT 정보가 없습니다.");
          hasAlertedRef.current = true;
        }
        return;
      }

      // 유효한 데이터일 경우: reset alert flag
      hasAlertedRef.current = false;

      formData.setFieldValue("currentItem", {
        lotId: lotData.lotId?.toString() ?? "",
        itemCode: lotData.itemCode ?? "",
        quantity: lotData?.quantity ?? "",
        locationCode: lotData.locationCode ?? "",
        locationName: lotData?.locationName ?? "",
      });
    },
  });

  const itemsForQuery = [formData.values.currentItem, ...formData.values.items];

  // 품목을 선택했을 때, 해당 품목의 정보를 가져오는 쿼리
  const inventoryQueries = useQueries({
    queries: itemsForQuery.map((item) => {
      const queryConditions: any[] = [];

      if (item.itemCode) {
        queryConditions.push({ itemCode: { $eq: item.itemCode } });
      }

      if (item.lotId) {
        queryConditions.push({ lotId: { $eq: getLotId(item.lotId) } });
      }

      const { queryKey, queryFn } = inventories.find({
        query: { $and: queryConditions, quantity: { $gt: 0 } },
        populate: ["lot", "item", "location", "itemWithGrade"],
      });

      return {
        queryKey,
        queryFn,
        enabled: !!item.itemCode,
      };
    }),
  });

  const locationData = useMemo(() => {
    const inventoryList = inventoryQueries[0]?.data?.data ?? [];

    return inventoryList
      .filter(
        (inv) =>
          inv.lotId?.toString() === getLotId(formData.values.currentItem.lotId) &&
          inv.locationCode === getLocationCode(formData.values.currentItem.lotId),
      )
      .map((inv) => ({
        value: inv.locationCode ?? "",
        label: inv.locationName,
      }));
  }, [inventoryQueries[0]?.data?.data, formData.values.currentItem.lotId]);

  const uniqueLocationData = locationData?.filter(
    (location, index, self) => index === self.findIndex((t) => t.value === location.value),
  );

  const itemData = useMemo(() => {
    return formData.values.items.map((item, index) => {
      const inventory = inventoryQueries[index + 1]?.data ?? [];
      const matched = Array.isArray(inventory)
        ? (inventory as any[]).find((inv) => inv?.itemCode === item.itemCode)
        : inventory?.data?.find((inv) => inv?.itemCode === item.itemCode);
      return matched?.itemWithGrade;
    });
  }, [formData.values.items, inventoryQueries]);

  const mutateRequest = (warnInsufficiency: boolean, formData: any, workId: number | undefined) => {
    subsidiaryMutate(
      {
        workId: workId as number,
        worksWorkIdBulkConsumeSubsidiaryPutRequest: {
          consumeSubsidiaryPairs: formData.values.items.map((item: any) => ({
            lotId: Number(getLotId(item.lotId)),
            sourceLocationCode: item.locationCode,
            quantity: item.quantity,
            warnInsufficiency,
          })),
        },
      },
      {
        onSuccess: (response) => {
          const successList = response.data.success;
          const failList = response.data.failed;

          const formatMsg = (
            list:
              | WorksWorkIdBulkConsumeSubsidiaryPut200ResponseSuccessInner[]
              | WorksWorkIdBulkConsumeSubsidiaryPut200ResponseFailedInner[]
              | undefined,
            isFail = false,
          ) =>
            list
              ?.map((res) =>
                res?.lotId
                  ? `${res.lotId}번 LOT (${res.quantity}개)`
                  : isFail
                    ? "알 수 없는 LOT"
                    : "",
              )
              .filter(Boolean)
              .join(", ");

          const successMsg = formatMsg(successList);
          const failMsg = formatMsg(failList, true);

          closeModal({});

          // 결과 분기 로직을 객체 매핑으로 단순화
          const resultCases: {
            condition: boolean;
            message: string;
            color: string;
          }[] = [
            {
              condition: failList?.length === 0,
              message: `모든 투입이 성공했습니다.\n${successMsg} 투입에 성공했습니다.`,
              color: "green",
            },
            {
              condition: successList?.length === 0,
              message: `모든 투입이 실패했습니다.\n${failMsg} 투입에 실패했습니다.`,
              color: "red",
            },
            {
              condition: (successList?.length as number) > 0 && (failList?.length as number) > 0,
              message: `일부 투입이 성공했습니다.\n${successMsg} 투입에 성공했습니다.\n${failMsg} 투입에 실패했습니다.`,
              color: "yellow",
            },
          ];

          const matchedCase = resultCases.find((c) => c.condition);
          if (matchedCase) {
            customAlert(matchedCase.message, "투입 결과", matchedCase.color);
          }
        },
        onError: (error: any) => {
          closeModal({});
          customAlert(
            `투입 중 오류가 발생했습니다.\n${error.response?.data?.message || error.message}`,
            "투입 오류",
            "red",
          );
        },
      },
    );
  };

  const onSave = () => {
    mutateRequest(true, formData, workId);
  };

  return (
    <MultiPigmentInput.Flex direction={"column"} gap={10} align={"center"} w="80rem" p="sm">
      <MultiPigmentInput.Flex direction={"column"} gap={10} w="100%">
        <MultiPigmentInput.Text size="lg">바코드 LOT 입력</MultiPigmentInput.Text>
        <MultiPigmentInput.Text size="xs">
          바코드를 스캔하여 자동으로 데이터를 입력합니다. <br /> 등록 버튼을 클릭하여 입고를
          완료합니다.
        </MultiPigmentInput.Text>
        <MultiPigmentInput.Flex gap={10} w="100%">
          <Input
            placeholder="바코드를 입력하세요"
            onKeyDown={handleKeyDown}
            value={tempLotId}
            data-autofocus
          />
        </MultiPigmentInput.Flex>
      </MultiPigmentInput.Flex>
      <Flex gap="md" w="100%">
        <ItemAutoComplete
          label="품목 선택"
          withAsterisk
          getItemType={[ItemsGet200ResponseRowsInnerItemTypeEnum.SUBSIDIARY]}
          {...formData.getInputProps(`currentItem.itemCode`)}
        />
        <Flex direction={"column"} gap="sm" w="50%">
          {lotId ? (
            <>
              <MultiPigmentInput.TextInput
                label="로트명"
                defaultValue={lotId}
                {...formData.getInputProps("currentItem.lotId")}
                disabled
              />
              <MultiPigmentInput.TextInput
                label="출고로케이션코드"
                defaultValue={lotId ? formData.values.currentItem.locationCode : ""}
                {...formData.getInputProps("currentItem.locationCode")}
                disabled
              />
            </>
          ) : (
            <>
              <MultiPigmentInput.InventoriesAutoComplete
                label="로트명"
                defaultValue={formData.values.currentItem.lotId}
                {...formData.getInputProps("currentItem.lotId")}
                disabled={!formData.values.currentItem.itemCode}
              />
              <MultiPigmentInput.LocationAutoComplete
                label="출고로케이션코드"
                defaultValue={formData.values.currentItem.locationCode}
                {...formData.getInputProps("currentItem.locationCode")}
                data={uniqueLocationData}
                disabled={!formData.values.currentItem.itemCode}
              />
            </>
          )}
          <MultiPigmentInput.TextInput
            label="수량"
            defaultValue={lotId ? formData.values.currentItem.quantity : ""}
            {...formData.getInputProps("currentItem.quantity")}
            disabled={!formData.values.currentItem.itemCode}
            type="number"
          />
        </Flex>
      </Flex>
      <Button
        w="100%"
        my="md"
        color="teal"
        onClick={() => {
          formData.insertListItem("items", formData.values.currentItem);
          formData.setFieldValue("currentItem", {
            itemCode: "",
            lotId: "",
            locationCode: "",
            locationName: "",
            quantity: "",
          });

          // 입력창 초기화
          tempLotId = "";
          setLotId("");
        }}
      >
        항목 추가
      </Button>
      <Table>
        <thead>
          <tr>
            <th>품목명</th>
            <th>Grade</th>
            <th>Color</th>
            <th>형태</th>
            <th>로트ID</th>
            <th>출고로케이션코드</th>
            <th>수량</th>
          </tr>
        </thead>
        <tbody>
          {formData?.values?.items.length > 0 ? (
            formData?.values?.items?.map((form, index) => {
              const items = itemData?.[index];

              return (
                <tr key={`${form.itemCode}-${index}`}>
                  <td>{items?.name}</td>
                  <td>{items?.gradeName}</td>
                  <td>{items?.itemCategoryName}</td>
                  <td>{items?.abstractItemCode}</td>
                  <td>{getLotId(form.lotId)}</td>
                  <td>{form.locationCode}</td>
                  <td>{form.quantity}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={7} style={{ textAlign: "center" }}>
                항목을 추가해주세요.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <MultiPigmentInput.Flex justify={"flex-end"} w="100%">
        <Button w="100%" onClick={() => onSave()}>
          투입
        </Button>
      </MultiPigmentInput.Flex>
    </MultiPigmentInput.Flex>
  );
};
